/**
 * QnaSection — Tab 3c (Rückfragen / Q&A; P2 Placement Slice 9). Query = usePlacementQna; every
 * mutation goes through the hook and invalidates the server state. One QnaThreadCard per submission
 * (submission order, stable). A closed Ausschreibung shows an amber read-only banner.
 */

import { useState } from 'react';
import { CountBadge } from '../../components/ui/CountBadge';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { QnaThreadCard } from './QnaThreadCard';
import {
  usePlacementQna,
  useAntwortenQna,
  useErledigtQna,
  useOffenQna,
  usePatchQnaFrist,
  useGelesenQna,
} from './usePlacementQna';

function codeOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function QnaSection({ placementId, canWrite }: { placementId: string; canWrite: boolean }) {
  const { can } = useEffectivePermissions();
  const canSend = canWrite && can('communication.send');

  const query = usePlacementQna(placementId);
  const antworten = useAntwortenQna(placementId);
  const erledigt = useErledigtQna(placementId);
  const offen = useOffenQna(placementId);
  const patchFrist = usePatchQnaFrist(placementId);
  const gelesen = useGelesenQna(placementId);

  const [sendingSid, setSendingSid] = useState<string | null>(null);
  const [errorBySid, setErrorBySid] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Lade Rückfragen…</p>;
  const data = query.data;
  if (!data) return null;

  const handleAntworten = async (sid: string, text: string, opts: { empfaenger_bestaetigt_an?: string }) => {
    setSendingSid(sid);
    setErrorBySid((m) => ({ ...m, [sid]: '' }));
    try {
      await antworten.mutateAsync({ sid, body: { text, ...(opts.empfaenger_bestaetigt_an !== undefined ? { empfaenger_bestaetigt_an: opts.empfaenger_bestaetigt_an } : {}) } });
    } catch (e) {
      setErrorBySid((m) => ({ ...m, [sid]: codeOf(e) }));
    } finally {
      setSendingSid(null);
    }
  };

  const run = async (p: Promise<unknown>) => {
    setBanner(null);
    try {
      await p;
    } catch (e) {
      setBanner(codeOf(e));
    }
  };

  return (
    <section className="flex flex-col gap-4" data-testid="placement-qna-section">
      <header className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold">Rückfragen (Q&amp;A)</h3>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <CountBadge count={data.offen} /> offen
        </span>
        <span className="flex items-center gap-1 text-xs text-status-danger">
          <CountBadge count={data.ueberfaellig} /> überfällig
        </span>
        <span className="flex items-center gap-1 text-xs text-status-info">
          <CountBadge count={data.ungelesen} /> ungelesen
        </span>
      </header>

      {!data.aktion_erlaubt && (
        <p className="rounded-md border border-status-warning-border bg-status-warning-bg p-2 text-sm text-status-warning" data-testid="qna-locked-banner">
          Ausschreibung abgeschlossen - Rückfragen nur lesend
        </p>
      )}
      {banner && <p className="text-sm text-status-danger" data-testid="qna-section-error">{banner}</p>}

      {data.items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          Noch keine Offertanfragen (Schritt 6)
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {data.items.map((item) => (
            <QnaThreadCard
              key={item.submission_id}
              item={item}
              canWrite={canWrite}
              canSend={canSend}
              aktionErlaubt={data.aktion_erlaubt}
              mailboxVerbunden={data.mailbox_verbunden}
              sending={sendingSid === item.submission_id}
              error={errorBySid[item.submission_id] || null}
              onAntworten={(text, opts) => void handleAntworten(item.submission_id, text, opts)}
              onErledigt={(grund) => void run(erledigt.mutateAsync({ sid: item.submission_id, body: { grund: grund || undefined } }))}
              onOffen={() => void run(offen.mutateAsync({ sid: item.submission_id }))}
              onFrist={(date) => void run(patchFrist.mutateAsync({ sid: item.submission_id, antwort_frist_date: date }))}
              onGelesen={() => void gelesen.mutateAsync({ sid: item.submission_id })}
            />
          ))}
        </div>
      )}
    </section>
  );
}
